/**
 * Queries that must never be written to the device's disk.
 *
 * The offline cache (ADR-0056) persists every successful query to IndexedDB
 * for a day, and that is right for a menu or a task list. It is wrong for a
 * vault: HQ's `ops.vault.*` reads carry a person's passphrase-locked private
 * key, the list of logins and who holds them, and the recovery key under its
 * paper code. On disk they outlive the session (and a logout) and hand
 * anyone with the device something to attack offline (#617 security review,
 * finding H1). So an app names the procedure prefixes it never persists; the
 * provider filters them out of every write, purges any already on disk when
 * it loads, and `forgetQueries` drops them from memory on lock or logout.
 */
import type { QueryClient, QueryKey } from '@tanstack/react-query';

/*
 * STRUCTURAL types, and the library's two default rules written out, because
 * the tree carries two copies of query-core (react-query's and the persist
 * client's); their classes' private fields make one copy's Query unassignable
 * to the other's. The defaults are: persist a query that succeeded, and a
 * mutation that is paused (offline).
 */
interface PersistableQuery {
  queryKey: QueryKey;
  state: { status: string; data?: unknown };
}
interface PersistableMutation {
  options: { mutationKey?: readonly unknown[] };
  state: { isPaused: boolean };
}

/** The tRPC procedure a query or mutation key belongs to, e.g. `ops.vault.me`. */
export function trpcPathOf(key: readonly unknown[] | undefined): string | null {
  const head = key?.[0];
  if (!Array.isArray(head) || head.length === 0) return null;
  if (!head.every((s): s is string => typeof s === 'string')) return null;
  return head.join('.');
}

/** Does this key fall under one of the never-persisted prefixes? */
export function isNeverPersisted(
  key: readonly unknown[] | undefined,
  prefixes: readonly string[],
): boolean {
  const path = trpcPathOf(key);
  return path !== null && prefixes.some((p) => path.startsWith(p));
}

/**
 * #840 — "who is signed in", answered "nobody".
 *
 * The sign-in screen asks `auth.getCurrentUser` and gets `null`; that answer is
 * written to the device cache like any other. Sign in, reload within a second,
 * and the page can restore that `null` before the signed-in answer has been
 * written over it. Restored data counts as fresh, so the shell's guard believes
 * it and sends the person back to the sign-in page without asking the server.
 *
 * A signed-out answer is worth nothing offline (there is nothing to show
 * without a session) and is wrong the moment anyone signs in, so it is never
 * written, and one restored from an older cache is dropped on load. The guard
 * then finds no answer and asks the server. A signed-IN answer still persists,
 * which is what lets the app draw at once, and offline.
 */
export const IDENTITY_PATH = 'auth.getCurrentUser';

export function isSignedOutAnswer(q: { queryKey: QueryKey; state: { data?: unknown } }): boolean {
  return trpcPathOf(q.queryKey) === IDENTITY_PATH && q.state.data == null;
}

/** Drop any restored signed-out answer, so the next read asks the server. */
export function forgetSignedOutAnswer(queryClient: QueryClient): void {
  queryClient.removeQueries({ predicate: (q) => isSignedOutAnswer(q) });
}

/** The `dehydrateOptions` that keep those queries and mutations off the disk. */
export function persistFilters(prefixes: readonly string[]): {
  shouldDehydrateQuery: (q: PersistableQuery) => boolean;
  shouldDehydrateMutation: (m: PersistableMutation) => boolean;
} {
  return {
    shouldDehydrateQuery: (q) =>
      q.state.status === 'success' &&
      !isNeverPersisted(q.queryKey, prefixes) &&
      !isSignedOutAnswer(q),
    shouldDehydrateMutation: (m) =>
      m.state.isPaused && !isNeverPersisted(m.options.mutationKey, prefixes),
  };
}

/**
 * Drop matching queries and mutations from memory. The persisted copy is
 * rewritten on the next cache change without them (and never had them once
 * the filter is in place). `keep` spares exact procedure paths, e.g. the one
 * a lock screen still needs.
 */
export function forgetQueries(
  queryClient: QueryClient,
  prefixes: readonly string[],
  opts: { keep?: readonly string[] } = {},
): void {
  const keep = new Set(opts.keep ?? []);
  queryClient.removeQueries({
    predicate: (q) => {
      const path = trpcPathOf(q.queryKey);
      return path !== null && !keep.has(path) && isNeverPersisted(q.queryKey, prefixes);
    },
  });
  const mutations = queryClient.getMutationCache();
  for (const m of mutations.getAll()) {
    if (isNeverPersisted(m.options.mutationKey, prefixes)) mutations.remove(m);
  }
}
